import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('ErrorBoundary:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary-screen">
          <h1 className="h-display">Something went sideways.</h1>
          <p className="subtitle">A small refresh usually fixes it.</p>
          <button className="btn-primary" onClick={() => window.location.reload()}>Reload</button>
        </div>
      );
    }
    return this.props.children;
  }
}
import './App.css';

import Login from './components/Login';
import AppLayout from './routes/app/AppLayout';
import TodayPage from './routes/app/TodayPage';
import FocusPage from './routes/app/FocusPage';
import PlansPage from './routes/app/PlansPage';
import InsightsPage from './routes/app/InsightsPage';
import ReflectionPage from './routes/app/ReflectionPage';
import ProfilePage from './routes/app/ProfilePage';
import BillingPage from './routes/billing/BillingPage';
import OnboardingPage from './routes/onboarding/OnboardingPage';
import AppAccessGuard from './components/AppAccessGuard';
import { apiGet } from './lib/api';
import { isNative } from './lib/native';
import { reconcileAllNotifications } from './lib/notifications';

const HomePage = lazy(() => {
  if (process.env.REACT_APP_PLATFORM === 'mobile') {
    return Promise.resolve({ default: () => <Navigate to="/app" replace /> });
  }
  return import('./routes/public/HomePage');
});

const AdminPage = lazy(() => {
  if (process.env.REACT_APP_PLATFORM === 'mobile') {
    return Promise.resolve({ default: () => <div>Admin not available in mobile build</div> });
  }
  return import('./routes/admin/AdminPage');
});
const AuditPage = lazy(() => {
  if (process.env.REACT_APP_PLATFORM === 'mobile') {
    return Promise.resolve({ default: () => <div>Audit logs not available in mobile build</div> });
  }
  return import('./routes/admin/AuditPage');
});
const BillingAdminPage = lazy(() => {
  if (process.env.REACT_APP_PLATFORM === 'mobile') {
    return Promise.resolve({ default: () => <div>Billing admin not available in mobile build</div> });
  }
  return import('./routes/admin/BillingAdminPage');
});
const BillingCardsAdminPage = lazy(() => {
  if (process.env.REACT_APP_PLATFORM === 'mobile') {
    return Promise.resolve({ default: () => <div>Billing cards admin not available in mobile build</div> });
  }
  return import('./routes/admin/BillingCardsAdminPage');
});
const BillingCardFormPage = lazy(() => {
  if (process.env.REACT_APP_PLATFORM === 'mobile') {
    return Promise.resolve({ default: () => <div>Billing cards admin not available in mobile build</div> });
  }
  return import('./routes/admin/BillingCardFormPage');
});

const IS_MOBILE_BUILD = process.env.REACT_APP_PLATFORM === 'mobile';

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const [userId, setUserId] = useState(null);
  const [user, setUser] = useState(null);
  const [authStatus, setAuthStatus] = useState('loading');
  const loginPendingRef = React.useRef(false);

  useEffect(() => { checkAuthentication(); }, []);

  const checkAuthentication = async () => {
    setAuthStatus('loading');
    try {
      const response = await apiGet('/api/auth/me');
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setUserId(userData.user_id);
        setAuthStatus('authenticated');
        syncNativeNotifications(userData);
        const intendedDestination = sessionStorage.getItem('intendedDestination');
        if (intendedDestination && location.pathname === '/') {
          sessionStorage.removeItem('intendedDestination');
          navigate(intendedDestination, { replace: true });
        }
      } else {
        setAuthStatus('unauthenticated');
      }
    } catch (error) {
      // Network failure vs unauthenticated: if offline, keep stored token and retry later
      if (error.message?.includes('Network') || !navigator.onLine) {
        setAuthStatus('offline');
      } else {
        setAuthStatus('unauthenticated');
      }
    }
  };

  const refreshMe = async () => {
    try {
      const response = await apiGet('/api/auth/me');
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setUserId(userData.user_id);
        return true;
      }
      return false;
    } catch (error) { return false; }
  };

  const syncNativeNotifications = async (userData) => {
    if (!isNative() || !userData?.user_id) return;
    try {
      const actsRes = await apiGet(`/api/activities/${userData.user_id}`);
      const activities = actsRes.ok ? await actsRes.json() : [];
      await reconcileAllNotifications({
        prefs: userData.notification_prefs || {},
        activities,
      });
    } catch (e) {}
  };

  const isSafeRedirect = (url) => {
    if (!url) return false;
    if (!url.startsWith('/')) return false;
    if (url.includes('http:') || url.includes('https:')) return false;
    if (url.startsWith('//')) return false;
    if (url.includes('@')) return false;
    return true;
  };

  const handleLogin = (userData) => {
    if (window.location.hash) window.location.hash = '';
    loginPendingRef.current = true;
    setUser(userData);
    setUserId(userData.user_id);
    setAuthStatus('authenticated');
    syncNativeNotifications(userData);
    // Navigation is handled by the useEffect below watching authStatus + user
  };

  // Navigate after login once authStatus and user are set in state
  useEffect(() => {
    if (authStatus !== 'authenticated' || !user || !loginPendingRef.current) return;
    loginPendingRef.current = false;
    const intendedDestination = sessionStorage.getItem('intendedDestination');
    const needsOnboarding = user.is_new_user === true || user.onboarding_completed === false;
    if (needsOnboarding) {
      sessionStorage.removeItem('intendedDestination');
      navigate('/onboarding', { replace: true });
    } else if (intendedDestination && isSafeRedirect(intendedDestination)) {
      sessionStorage.removeItem('intendedDestination');
      navigate(intendedDestination, { replace: true });
    } else {
      if (intendedDestination) sessionStorage.removeItem('intendedDestination');
      navigate('/app', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus, user]);

  const AdminRoute = ({ children }) => {
    const hasAdminRole = user && user.admin_roles && user.admin_roles.length > 0;
    if (!hasAdminRole) return <Navigate to="/app" replace />;
    return children;
  };

  const OnboardingRoute = ({ children }) => {
    if (authStatus === 'loading') return <div className="app-loading"><div className="loading-spinner" /><p>Loading…</p></div>;
    if (authStatus === 'unauthenticated') return <Navigate to="/" replace />;
    if (user && user.onboarding_completed === true && user.is_new_user === false) return <Navigate to="/app" replace />;
    return children;
  };

  const AppAccessRoute = ({ children }) => {
    if (authStatus === 'loading') return <div className="app-loading"><div className="loading-spinner" /><p>Loading…</p></div>;
    if (authStatus === 'unauthenticated') return <Navigate to="/" replace />;
    const needsOnboarding = user && (user.is_new_user === true || user.onboarding_completed === false);
    if (needsOnboarding) return <Navigate to="/onboarding" replace />;
    return children;
  };

  const ProtectedRoute = ({ children }) => {
    const loc = useLocation();
    if (authStatus === 'loading') return <div className="app-loading"><div className="loading-spinner" /><p>Loading…</p></div>;
    // 'offline' — trust stored token, allow through; offline banner shown globally
    if (authStatus === 'unauthenticated') {
      const intendedPath = loc.pathname + loc.search;
      if (intendedPath !== '/') sessionStorage.setItem('intendedDestination', intendedPath);
      return <Login onLogin={handleLogin} />;
    }
    if (!userId || !user) return <div className="app-loading"><div className="loading-spinner" /><p>Setting up…</p></div>;
    return children;
  };

  return (
    <Routes>
      <Route path="/" element={
        <Suspense fallback={<div style={{ minHeight: '100vh', background: 'var(--paper)' }} />}>
          <HomePage />
        </Suspense>
      } />

      <Route path="/app" element={
        <ProtectedRoute>
          <AppAccessRoute>
            <AppAccessGuard />
          </AppAccessRoute>
        </ProtectedRoute>
      }>
        <Route element={<AppLayout userId={userId} user={user} refreshMe={refreshMe} />}>
          <Route index element={<Navigate to="/app/today" replace />} />
          <Route path="today" element={<TodayPage />} />
          <Route path="focus" element={<FocusPage />} />
          <Route path="plans" element={<PlansPage />} />
          <Route path="insights" element={<InsightsPage />} />
          <Route path="reflection" element={<ReflectionPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="settings" element={<ProfilePage />} />
          <Route path="settings/:section" element={<ProfilePage />} />
          <Route path="chat" element={<Navigate to="/app/reflection" replace />} />
          <Route path="ai" element={<Navigate to="/app/reflection" replace />} />
          <Route path="week" element={<Navigate to="/app/plans" replace />} />
          <Route path="habits" element={<Navigate to="/app/profile" replace />} />
          <Route path="routines" element={<Navigate to="/app/profile" replace />} />
          <Route path="plan" element={<Navigate to="/app/plans" replace />} />
        </Route>
      </Route>

      <Route path="/onboarding" element={<OnboardingRoute><OnboardingPage /></OnboardingRoute>} />
      <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />

      {!IS_MOBILE_BUILD && (
        <>
          <Route path="/admin" element={<ProtectedRoute><AdminRoute><Suspense fallback={<div className="app-loading"><div className="loading-spinner" /></div>}><AdminPage userId={userId} user={user} /></Suspense></AdminRoute></ProtectedRoute>} />
          <Route path="/admin/audit" element={<ProtectedRoute><AdminRoute><Suspense fallback={<div className="app-loading"><div className="loading-spinner" /></div>}><AuditPage userId={userId} user={user} /></Suspense></AdminRoute></ProtectedRoute>} />
          <Route path="/admin/billings" element={<ProtectedRoute><AdminRoute><Suspense fallback={<div className="app-loading"><div className="loading-spinner" /></div>}><BillingAdminPage /></Suspense></AdminRoute></ProtectedRoute>} />
          <Route path="/admin/billing-cards" element={<ProtectedRoute><AdminRoute><Suspense fallback={<div className="app-loading"><div className="loading-spinner" /></div>}><BillingCardsAdminPage /></Suspense></AdminRoute></ProtectedRoute>} />
          <Route path="/admin/billing-cards/new" element={<ProtectedRoute><AdminRoute><Suspense fallback={<div className="app-loading"><div className="loading-spinner" /></div>}><BillingCardFormPage /></Suspense></AdminRoute></ProtectedRoute>} />
          <Route path="/admin/billing-cards/:cardId" element={<ProtectedRoute><AdminRoute><Suspense fallback={<div className="app-loading"><div className="loading-spinner" /></div>}><BillingCardFormPage /></Suspense></AdminRoute></ProtectedRoute>} />
        </>
      )}

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function NativeExternalLinkBridge() {
  // On native (Capacitor) Android, a plain `<a target="_blank">` opens
  // inside the WebView with no chrome — which traps the user. Intercept
  // such clicks at the document level and route them through
  // @capacitor/browser (a Custom Tab) so Terms / Privacy / Support
  // links etc. behave correctly on Android.
  useEffect(() => {
    if (!isNative()) return undefined;
    let cancelled = false;
    let BrowserRef = null;
    (async () => {
      try {
        const mod = await import('@capacitor/browser');
        if (!cancelled) BrowserRef = mod.Browser;
      } catch (e) {
        // plugin missing — leave default behaviour
      }
    })();

    const onClick = (e) => {
      if (!BrowserRef) return;
      // Walk up to find the nearest <a>
      const anchor = e.target && e.target.closest && e.target.closest('a');
      if (!anchor) return;
      const target = anchor.getAttribute('target');
      if (target !== '_blank') return;
      const href = anchor.getAttribute('href');
      if (!href) return;
      // Only intercept absolute http(s) URLs — skip mailto:, tel:, hash, etc.
      if (!/^https?:\/\//i.test(href)) return;
      e.preventDefault();
      try { BrowserRef.open({ url: href }); } catch (_) {}
    };

    document.addEventListener('click', onClick, true);
    return () => {
      cancelled = true;
      document.removeEventListener('click', onClick, true);
    };
  }, []);
  return null;
}

function NativeDeepLinkBridge() {
  useEffect(() => {
    let cleanup = null;
    let cancelled = false;
    (async () => {
      try {
        const native = await import('./lib/native');
        if (!native.isNative()) return;
        const [{ App: CapApp }, { Browser }] = await Promise.all([
          import('@capacitor/app'),
          import('@capacitor/browser'),
        ]);
        const listener = await CapApp.addListener('appUrlOpen', async (event) => {
          try {
            const url = event && event.url;
            if (!url || !url.startsWith('com.hourtrix.app://')) return;
            try { await Browser.close(); } catch (e) {}
            const u = new URL(url);
            const tok = u.searchParams.get('token');
            if (!tok) return;
            if (url.includes('oauth/callback') || u.host === 'oauth') {
              await native.setStoredToken(tok);
              window.location.href = '/app';
              return;
            }
            if (url.includes('verify') || u.host === 'verify') {
              window.location.href = `/?token=${encodeURIComponent(tok)}`;
            }
          } catch (e) {}
        });
        if (cancelled) listener && listener.remove && listener.remove();
        else cleanup = () => listener && listener.remove && listener.remove();
      } catch (e) {}
    })();
    return () => {
      cancelled = true;
      if (cleanup) cleanup();
    };
  }, []);
  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <NativeDeepLinkBridge />
        <NativeExternalLinkBridge />
        <AppRoutes />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
